'use strict';
const { getDependencyClass } = require('../../lib/dependencies.js');

/**
 *
 * @param {Element} element
 */
function getEmbedType(element) {
    const tagName = element.tagName.toLowerCase();
    const regex = /^phoenix-(.+)-embed$/i;
    const match = tagName.match(regex);
    if (match) {
        return match[1];
    } else if (element.hasAttribute('block-on-gdpr-vendor-consent')) {
        return element.getAttribute('block-on-gdpr-vendor-consent');
    }

    return 'other';
}

const OUTBRAIN_VISIBLE = 'phoenix:outbrain:in-view';
const EMBED_TAGS = [
    'phoenix-embedcode-embed',
    'phoenix-embedly-embed',
    'phoenix-facebook-embed',
    'phoenix-instagram-embed',
    'phoenix-metabet-embed',
    'phoenix-pinterest-embed',
    'phoenix-polldaddy-embed',
    'phoenix-reddit-embed',
    'phoenix-rewardstyle-embed',
    'phoenix-sendtonews-embed',
    'phoenix-sportradar-embed',
    'phoenix-tiktok-embed',
    'phoenix-twitter-embed',
    'phoenix-vine-embed',
    'phoenix-iframe',
];

const EXCO_VIDEO_TAG = 'phoenix-exco-player';
const STN_VIDEO_TAG = 'phoenix-sendtonews-embed';
const JW_PLAYER_TAG = 'phoenix-video';

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * Embed Height Tracking
     *
     * This component is invoked in `template-modules/outbrain/outbrain.js`.
     * It is listening for an event emitted by `<phoenix-outbrain>`, when it
     * is in view.
     * This component is responsible for collecting the heights of all ads and
     * embeds on the page, and sending them to LiftIgniter.
     */
    class EmbedHeightTracking extends pageClass.PhoenixElement {
        /** @type {HTMLElement} */
        contentElement = document;

        get observedBroadcastedEvents() {
            return [OUTBRAIN_VISIBLE];
        }

        broadcastedEventCallback(name, event = {}) {
            if (name === OUTBRAIN_VISIBLE && event.target) {
                this._trackElements(event.target);
            }
        }

        /**
         *
         * @param {PhoenixElement} target
         */
        _trackElements(target) {
            const currentRect = target.getBoundingClientRect();
            const featuredMediaPlatform = window.phxPageTarget?.config?.global?.config?.featuredMediaPlatform;

            const { heightOfPageUntilOutbrain, widgetId } = this._collectPageHeightAboveWidget(target);
            const adElementRects = this._collectAdElements(currentRect);
            const embedElementRects = this._collectEmbedElements(currentRect);
            const embedVideos = this._collectVideoElements(featuredMediaPlatform);

            if (window.$p) {
                window.$p('send', 'embed_heights', {
                    content_above_outbrain_height: JSON.stringify(heightOfPageUntilOutbrain),
                    ad_heights: JSON.stringify(adElementRects),
                    embed_heights: JSON.stringify(embedElementRects),
                    embed_videos: JSON.stringify(embedVideos),
                });
            }

            window.googleAnalyticsPromise.then(() => {
                window.googleAnalytics.sendEvent(
                    'PageDimension', // category
                    'OutbrainDepth', // action
                    widgetId, // label
                    heightOfPageUntilOutbrain, // value,
                    { nonInteraction: true } // dimensions
                );
            });
        }

        _collectEmbedElements(rect) {
            const query = EMBED_TAGS.join(', ');
            const elements = this._elementsWithRect(query, rect);

            return {
                count: elements.length,
                embeds: elements.reduce((src, [elem, rect]) => {
                    const embedType = getEmbedType(elem);
                    const height = rect.height;
                    return {
                        ...src,
                        [embedType]: (src[embedType] || 0) + height,
                    };
                }, {}),
            };
        }

        _collectVideoElements(featuredMediaPlatform) {
            const exCoVideoElements = document.getElementsByTagName(EXCO_VIDEO_TAG);
            const stnVideoElements = document.getElementsByTagName(STN_VIDEO_TAG);
            const jwPlayerElements = document.getElementsByTagName(JW_PLAYER_TAG);

            let count = 0;
            let videos = [];
            Array.from(exCoVideoElements).forEach(() => {
                videos.push({ type: 'exco' });
                count += 1;
            });
            Array.from(stnVideoElements).forEach(() => {
                videos.push({ type: 'stn' });
                count += 1;
            });
            Array.from(jwPlayerElements).forEach(() => {
                videos.push({ type: 'jwPlayer' });
                count += 1;
            });

            // Translates some featuredMediaPlatform values
            const mapping = {
                excoPlayer: 'exco',
                jwplayerPlaylist: 'jwplayer',
            };
            featuredMediaPlatform = mapping[featuredMediaPlatform] || featuredMediaPlatform;

            let featuredAssigned = !featuredMediaPlatform;
            videos.forEach((video) => {
                if (!featuredAssigned && video.type.toLowerCase() === featuredMediaPlatform?.toLowerCase()) {
                    video.placement = 'featured';
                    featuredAssigned = true;
                } else {
                    video.placement = 'inline';
                }
            });

            return {
                count,
                videos,
            };
        }

        _collectAdElements(rect) {
            const elements = this._elementsWithRect(`phoenix-ad.is-loaded`, rect);
            return {
                count: elements.length,
                height: elements.map(([, rect]) => rect.height),
            };
        }

        _collectPageHeightAboveWidget(outbrainElement) {
            const outbrainRect = outbrainElement.getBoundingClientRect();
            const widgetId = outbrainElement.querySelector('.OUTBRAIN').getAttribute('data-widget-id');
            let heightOfPageUntilOutbrain = Math.ceil(outbrainRect.top + window.pageYOffset);

            return { heightOfPageUntilOutbrain, widgetId };
        }

        /**
         * Find all elements on the page that match the selector, and are above the parentRect
         * @param {String} selector
         * @param {DOMRect} parentRect
         * @returns {Array<[Element, DOMRect]>}
         */
        _elementsWithRect(selector, parentRect) {
            if (!this.contentElement) {
                return [];
            }

            const elements = Array.from(this.contentElement.querySelectorAll(selector));

            return elements
                .map((el) => [el, el.getBoundingClientRect()])
                .filter(([, rect]) => rect.height > 0 && rect.top < parentRect.top);
        }
    }

    window.customElements.define('phoenix-embed-tracking', EmbedHeightTracking);
});
